import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc,
  serverTimestamp, Timestamp, deleteField,
} from 'firebase/firestore';
import { db } from './firebase';
import { ScheduledTask, ScheduledTaskPeriod, ScheduledTaskSchedule, ScheduledTaskType, ReportType } from '../types';

function mapTaskDoc(d: { id: string; data: () => Record<string, unknown> }): ScheduledTask {
  const data = d.data();
  const toDate = (v: unknown): Date => {
    if (!v) return new Date();
    if (v instanceof Timestamp) return v.toDate();
    const obj = v as { toDate?: () => Date };
    if (typeof obj.toDate === 'function') return obj.toDate();
    return new Date();
  };
  return {
    id: d.id,
    type: (data.type as ScheduledTaskType) ?? 'report',
    enabled: (data.enabled as boolean) ?? false,
    schedule: (data.schedule as ScheduledTaskSchedule) ?? 'daily',
    dayOfWeek: data.dayOfWeek !== undefined ? (data.dayOfWeek as number) : undefined,
    dayOfMonth: data.dayOfMonth !== undefined ? (data.dayOfMonth as number) : undefined,
    reportType: (data.reportType as ReportType) ?? 'current_quantity',
    recipients: (data.recipients as string[]) ?? [],
    period: (data.period as ScheduledTaskPeriod) ?? 'none',
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    createdBy: (data.createdBy as string) ?? '',
  };
}

export async function getScheduledTasks(): Promise<ScheduledTask[]> {
  const snap = await getDocs(collection(db, 'scheduledTasks'));
  return snap.docs.map(mapTaskDoc).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export async function createScheduledTask(
  task: Omit<ScheduledTask, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const payload = Object.fromEntries(
    Object.entries({ ...task, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
      .filter(([, v]) => v !== undefined),
  );
  const ref = await addDoc(collection(db, 'scheduledTasks'), payload);
  return ref.id;
}

export async function updateScheduledTask(
  id: string,
  task: Partial<Omit<ScheduledTask, 'id' | 'createdAt' | 'createdBy'>>,
): Promise<void> {
  const payload = Object.fromEntries(
    Object.entries({ ...task, updatedAt: serverTimestamp() })
      .map(([k, v]) => [k, v === undefined ? deleteField() : v]),
  );
  await updateDoc(doc(db, 'scheduledTasks', id), payload);
}

export async function deleteScheduledTask(id: string): Promise<void> {
  await deleteDoc(doc(db, 'scheduledTasks', id));
}

export async function toggleScheduledTask(id: string, enabled: boolean): Promise<void> {
  await updateDoc(doc(db, 'scheduledTasks', id), {
    enabled,
    updatedAt: serverTimestamp(),
  });
}
